import { ReactElement } from "react";
import Chart from "../../components/charts/chart";
import { useChartDimensions } from "./chartUtils";
import { IGaugeConfig, ISegment } from "./types";
import { getPalette } from "../../configuration/configuration";
import { useSelector } from "react-redux";
import { IState } from "../../types";
import { useTheme } from "@mui/material";

interface IProductionChartProps {
  keyValue: string;
  gaugeConfig: IGaugeConfig;
  value: number;
}

interface IPoint {
  x: number;
  y: number;
}

const degree90 = 90;
const degree180 = 180;

const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number): IPoint => {
  const angleInRadians = (angleInDegrees - degree90) * Math.PI / degree180;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians)
  };
};

const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {

  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);

  const largeArcFlag = endAngle - startAngle <= degree180 ? "0" : "1";

  return [
    "M", start.x, start.y,
    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(" ");
};

const GaugeChart = ({ gaugeConfig, value, keyValue }: IProductionChartProps): ReactElement => {
  const darkMode = useSelector((state: IState) => state.darkMode);
  const theme = useTheme();

  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: 100,
    width: 200,
    marginBottom: 10,
    marginLeft: 10,
    marginRight: 10,
    marginTop: 10
  });

  const radius = Math.min(dimensions.boundedWidth, dimensions.boundedHeight) / 2.0;
  const centerX = dimensions.boundedWidth / 2.0;
  const centerY = dimensions.boundedHeight / 2.0;

  const currentSegment = gaugeConfig.segments.find(x => x.start <= value && x.end >= value);
  const fixedValue = Math.min(Math.max(gaugeConfig.minimum, value), gaugeConfig.maximum);
  const diff = gaugeConfig.maximum - gaugeConfig.minimum;
  const factorValue = (fixedValue - gaugeConfig.minimum) / (diff ? diff : 1);
  const angleValue = gaugeConfig.start + factorValue * gaugeConfig.length;

  const path: any = gaugeConfig.segments.map((segment: ISegment, index: number) => {
    const startSeg = (segment.start - gaugeConfig.minimum) / diff;
    const endSeg = (segment.end - gaugeConfig.minimum) / diff;

    const startAngle = gaugeConfig.start + startSeg * gaugeConfig.length;
    const endAngle = gaugeConfig.start + endSeg * gaugeConfig.length;

    return (
      <g key={`gauge-arc-${keyValue}-${index}`}>
        <path d={describeArc(centerX, centerY, radius, startAngle, endAngle)}
          fill="none"
          stroke={segment.color}
          strokeWidth={20}/>
      </g>
    );
  });

  const getSegmentValue = (v: number, index: number) => {
    const fv = (v - gaugeConfig.minimum) / diff;
    const av = gaugeConfig.start + fv * gaugeConfig.length;
    const point = polarToCartesian(centerX, centerY, radius + 30, av);

    return (
      <text key={`val-seg-${index}`} x={point.x + 2} y={point.y} fill={theme.palette.text.primary}
        textAnchor="middle"
        style={{ fontSize: "0.8em" }}>{gaugeConfig.format ? gaugeConfig.format(v) : v}</text>);
  };


  return (
    <div ref={ref} className="text-center" >
      <Chart dimensions={dimensions}>
        <defs>
          <filter id="drop-shadow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="3" dy="3" result="offsetblur"/>
            <feFlood floodColor="#505050"/>
            <feComposite in2="offsetblur" operator="in"/>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {path}

        <g>
          { gaugeConfig.segments.map((item, index) => getSegmentValue(item.start, index))}
          {getSegmentValue(gaugeConfig.maximum, gaugeConfig.segments.length)}
        </g>

        <g style={{ filter: "url(#drop-shadow)" }}>
          <polygon id="Path-3"
            stroke={darkMode ? getPalette().light.background : getPalette().dark.background}
            strokeWidth="1"
            fill={darkMode ? getPalette().light.background : getPalette().dark.background}
            points={`${centerX} ${centerY - radius} ${centerX - 5} ${centerY} ${centerX + 5} ${centerY}`}
            transform={`rotate(${angleValue} ${centerX} ${centerY})`}/>

          <circle cx={centerX} cy={centerY} r={10} fill={darkMode ? getPalette().light.background : getPalette().dark.background}/>
        </g>

        <text y={dimensions.boundedHeight - 14} x={centerX}
          fill={currentSegment !== undefined ? currentSegment.color :  "#2e7d32"}
          fontWeight="bold"
          style={{
            textAnchor: "middle",
            fontSize: "0.9em"
          }}>{gaugeConfig.format ? gaugeConfig.format(value) : value}</text>

        <text y={dimensions.boundedHeight} x={centerX}
          fill={currentSegment !== undefined ? currentSegment.color :  "#2e7d32"}
          fontWeight="bold"
          style={{
            textAnchor: "middle",
            fontSize: "0.9em"
          }}>{keyValue}</text>
      </Chart>
    </div>
  );
};

export default GaugeChart;
